/**
 * Композер аффирмаций на правилах (русский язык). Работает офлайн на клиенте
 * и как fallback на сервере, если LLM не настроен или недоступен.
 *
 * Детерминированный: одинаковый ввод → одинаковые фразы. Это важно для кэша TTS —
 * шаблонные аффирмации совпадают у разных пользователей и озвучиваются один раз.
 *
 * Мини-DSL в шаблонах: "{м|ж}" — вариант для мужского/женского рода.
 * Для нейтрального обращения шаблоны с вариантами пропускаются.
 */
import { THEME_DEFS, type ThemeDef } from "./affirmations-ru.js";
import type { Gender } from "./schemas.js";

export interface ComposeOptions {
  gender?: Gender;
  max?: number;
  /** 0 — канонический порядок (лучшие фразы первыми); другое число — другая подборка */
  seed?: number;
  /** фразы, которые уже показывали — при обновлении подборки их не повторяем */
  exclude?: string[];
}

export interface ComposedAffirmation {
  text: string;
  source: "own" | "transform" | "theme";
  theme?: ThemeId;
}

export type ThemeId = string;

const norm = (s: string) => s.toLowerCase().replace(/ё/g, "е");

interface CompiledTheme extends ThemeDef {
  exact: Set<string>;
  prefixes: string[];
  phrasesKeys: string[];
}

const COMPILED: CompiledTheme[] = THEME_DEFS.map((t) => ({
  ...t,
  exact: new Set(t.keys.filter((k) => k.startsWith("=")).map((k) => norm(k.slice(1)))),
  prefixes: t.keys.filter((k) => !k.startsWith("=") && !k.trim().includes(" ")).map((k) => norm(k.trim())),
  phrasesKeys: t.keys.filter((k) => k.trim().includes(" ")).map((k) => norm(k.trim())),
}));

/** Насколько запрос относится к теме: число разных совпавших ключей (фразы весят вдвое больше) */
export function scoreThemes(input: string): { id: ThemeId; score: number }[] {
  const text = ` ${norm(input).replace(/[^а-яa-z0-9]+/g, " ").trim()} `;
  const tokens = text.trim().split(" ").filter(Boolean);
  const out: { id: ThemeId; score: number }[] = [];
  for (const t of COMPILED) {
    let score = 0;
    for (const p of t.prefixes) if (tokens.some((w) => w.startsWith(p))) score += 1;
    for (const e of t.exact) if (tokens.includes(e)) score += 1;
    for (const ph of t.phrasesKeys) if (text.includes(` ${ph}`)) score += 2;
    if (score > 0) out.push({ id: t.id, score });
  }
  return out.sort((a, b) => b.score - a.score || THEME_ORDER.get(a.id)! - THEME_ORDER.get(b.id)!);
}

const THEME_ORDER = new Map(THEME_DEFS.map((t, i) => [t.id, i]));

/** Совместимость: список тем в старом формате */
export const THEMES = COMPILED.map((t) => ({ id: t.id, phrases: t.phrases }));

function mulberry32(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function shuffled<T>(arr: T[], seed: number): T[] {
  if (!seed) return [...arr];
  const rnd = mulberry32(seed);
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1));
    [a[i], a[j]] = [a[j]!, a[i]!];
  }
  return a;
}

/** Команды вида «напиши аффирмации на …» — убираем, оставляем суть запроса */
const REQUEST = /^\s*(?:пожалуйста[,\s]+)?(?:напиши|составь|сделай|дай|придумай|подбери|нужны|хочу|мне нужны)?\s*(?:мне\s+)?(?:\d+\s+)?(?:аффирмаци[яийю]|установк[иау]|настро[йя])\s*(?:на|для|про|о|об|чтобы)?\s*/iu;

export function stripRequest(input: string): string {
  return input.replace(REQUEST, "").trim() || input.trim();
}

const DESIRE =
  /^(?:я\s+)?(?:очень\s+|так\s+|правда\s+|сильно\s+)?(?:хочу|хотел[аи]?\s+бы|хотелось\s+бы|мечтаю|планирую|стремлюсь|желаю|собираюсь|буду|(?:мне\s+)?(?:надо|нужно|необходимо))(?:\s+|$)/iu;
const NEGATIVE = /^(?:перестать|прекратить|бросить|избавиться|не\s|меньше\s|забыть|отказаться)/iu;
const OWN_NEGATIVE = /^я\s+(?:не\s|боюсь|устал|ненавиж|злюсь|переживаю|волнуюсь|тревожусь|никогда)/iu;
const INFINITIVE = /^[а-яё-]+(?:ть|ться|ти|тись|чь|чься)(?=[\s,]|$)/iu;

export function applyGender(template: string, gender: Gender): string | null {
  if (!template.includes("{")) return template;
  if (gender === "n") return null;
  return template.replace(/\{([^|}]*)\|([^}]*)\}/g, (_, m: string, f: string) => (gender === "m" ? m : f));
}

function finalize(text: string): string {
  let t = text.replace(/\s+/g, " ").trim().replace(/[\s,;:—-]+$/u, "");
  if (!t) return t;
  t = t[0]!.toLocaleUpperCase("ru") + t.slice(1);
  if (!/[.!?…]$/u.test(t)) t += ".";
  return t;
}

function dedupeKey(text: string): string {
  return text.toLowerCase().replace(/ё/g, "е").replace(/[^а-яa-z0-9]+/gu, "");
}

/** Разбивает свободный текст на смысловые фрагменты. */
export function splitClauses(input: string): string[] {
  const sentences = input
    .split(/[.!?;\n]+/u)
    .map((s) => s.trim())
    .filter(Boolean);
  const out: string[] = [];
  for (const s of sentences) {
    const pieces = s.split(/\s*,\s*|\s+а\s+также\s+|\s+и\s+(?=[а-яё-]+(?:ть|ться|ти|тись|чь|чься)(?:\s|$))/iu);
    let current: string[] = [];
    for (const raw of pieces) {
      const p = raw.trim();
      if (!p) continue;
      const lower = p.toLowerCase();
      const startsNew = current.length === 0 || DESIRE.test(lower) || INFINITIVE.test(lower) || /^я\s/iu.test(lower);
      if (startsNew) {
        if (current.length) out.push(current.join(", "));
        current = [p];
      } else {
        current.push(p);
      }
    }
    if (current.length) out.push(current.join(", "));
  }
  return out;
}

/** Превращает одно желание в аффирмацию в настоящем времени, если это можно сделать без ошибок. */
export function transformClause(clause: string, inheritedDesire: boolean): { text: string | null; desire: boolean; own: boolean } {
  let c = clause.trim();
  const lower = c.toLowerCase();

  if (/^я\s/iu.test(lower) && !DESIRE.test(lower)) {
    return { text: OWN_NEGATIVE.test(lower) ? null : finalize(c), desire: false, own: true };
  }

  const m = DESIRE.exec(c);
  const desire = Boolean(m) || inheritedDesire;
  if (m) c = c.slice(m[0].length).trim();
  if (!desire || !c) return { text: null, desire, own: false };

  const rest = c.toLowerCase();
  if (NEGATIVE.test(rest) || rest.startsWith("чтобы")) return { text: null, desire, own: false };
  if (c.length > 120) return { text: null, desire, own: false };

  let r: RegExpExecArray | null;
  if ((r = /^стать\s+(.+)$/iu.exec(c))) return { text: finalize(`С каждым днём я становлюсь ${r[1]}`), desire, own: false };
  if ((r = /^быть\s+(.+)$/iu.exec(c))) return { text: finalize(`Я позволяю себе быть ${r[1]}`), desire, own: false };
  if ((r = /^научиться\s+(.+)$/iu.exec(c))) return { text: finalize(`Я учусь ${r[1]}, и у меня получается`), desire, own: false };
  if (INFINITIVE.test(c)) return { text: finalize(`Мне легко ${c[0]!.toLowerCase()}${c.slice(1)}`), desire, own: false };
  return { text: null, desire, own: false };
}

export function detectThemes(input: string): ThemeId[] {
  return scoreThemes(input)
    .filter((t) => t.id !== "general")
    .map((t) => t.id);
}

const MAX_THEMES = 4;

export function composeAffirmations(input: string, opts: ComposeOptions = {}): ComposedAffirmation[] {
  const gender = opts.gender ?? "n";
  const max = opts.max ?? 8;
  const seed = opts.seed ?? 0;
  const excluded = new Set((opts.exclude ?? []).map(dedupeKey));
  const result: ComposedAffirmation[] = [];
  const seen = new Set<string>();
  const push = (a: ComposedAffirmation, allowExcluded = false) => {
    const key = dedupeKey(a.text);
    if (!key || seen.has(key) || result.length >= max) return false;
    if (!allowExcluded && excluded.has(key)) return false;
    seen.add(key);
    result.push(a);
    return true;
  };

  const query = stripRequest(input);

  // 1. Собственные формулировки человека, переведённые в настоящее время
  let desire = false;
  for (const clause of splitClauses(query)) {
    const t = transformClause(clause, desire);
    desire = t.desire;
    if (t.text) push({ text: t.text, source: t.own ? "own" : "transform" }, true);
  }

  // 2. Фразы из словаря по самым подходящим темам, места — пропорционально релевантности
  const scored = scoreThemes(query)
    .filter((t) => t.id !== "general")
    .slice(0, MAX_THEMES);
  const pool = scored.length ? scored : [{ id: "general", score: 1 }];
  const left = Math.max(0, max - result.length);
  const total = pool.reduce((s, t) => s + t.score, 0);
  const quota = pool.map((t) => Math.max(1, Math.round((left * t.score) / total)));

  const queues = pool.map((t, i) => {
    const def = COMPILED.find((c) => c.id === t.id)!;
    const phrases = def.phrases
      .map((p) => applyGender(p, gender))
      .filter((p): p is string => p !== null)
      .map(finalize);
    // разные seed для разных тем, чтобы подборки не «шагали» синхронно
    return { id: t.id, quota: quota[i]!, phrases: shuffled(phrases, seed ? seed * 31 + i : 0), taken: 0, pos: 0 };
  });

  const takeNext = (q: (typeof queues)[number]) => {
    while (q.pos < q.phrases.length) {
      const text = q.phrases[q.pos++]!;
      if (push({ text, source: "theme", theme: q.id })) {
        q.taken++;
        return true;
      }
    }
    return false;
  };

  // по кругу: каждая тема получает свою долю
  let progress = true;
  while (result.length < max && progress) {
    progress = false;
    for (const q of queues) if (q.taken < q.quota && result.length < max && takeNext(q)) progress = true;
  }
  // добираем из тех же тем сверх квоты, затем из близких тем, затем из общих фраз
  for (const q of queues) while (result.length < max && takeNext(q)) {}
  if (result.length < max) {
    const used = new Set(pool.map((t) => t.id));
    const related = pool.flatMap((t) => COMPILED.find((c) => c.id === t.id)?.related ?? []).filter((id) => !used.has(id) && id !== "general");
    for (const [i, id] of [...new Set(related)].entries()) {
      const def = COMPILED.find((c) => c.id === id);
      if (!def || result.length >= max) continue;
      const phrases = def.phrases.map((p) => applyGender(p, gender)).filter((p): p is string => p !== null).map(finalize);
      const q = { id, quota: max, taken: 0, pos: 0, phrases: shuffled(phrases, seed ? seed * 17 + i + 1 : 0) };
      // по две фразы из каждой близкой темы, чтобы подборка оставалась разнообразной
      for (let k = 0; k < 2 && result.length < max; k++) takeNext(q);
    }
  }
  if (result.length < max) {
    const general = COMPILED.find((c) => c.id === "general")!;
    const g = { id: "general", quota: max, taken: 0, pos: 0, phrases: shuffled(general.phrases.map((p) => applyGender(p, gender)).filter((p): p is string => p !== null).map(finalize), seed) };
    while (result.length < max && takeNext(g)) {}
  }
  return result;
}

/** Все шаблонные фразы во всех родах — для прогрева TTS-кэша. */
export function allTemplatePhrases(): string[] {
  const set = new Set<string>();
  for (const t of COMPILED)
    for (const p of t.phrases)
      for (const g of ["m", "f", "n"] as const) {
        const v = applyGender(p, g);
        if (v) set.add(finalize(v));
      }
  return [...set];
}

export function suggestTitle(input: string): string {
  const [a, b] = detectThemes(stripRequest(input));
  const title = (id: string) => THEME_DEFS.find((t) => t.id === id)?.title ?? "Мой настрой";
  if (!a) return title("general");
  const ta = title(a);
  const tb = b ? title(b) : null;
  // «Энергия и глубокий сон», но не «Тело и форма и отпускание»
  return tb && !ta.includes(" и ") && !tb.includes(" и ") ? `${ta} и ${tb.toLowerCase()}` : ta;
}
